import React from 'react'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'
import Navigation from '../components/navigation'
import Footer from '../components/footer'
import './llm-security-testing.css'

const LLMSecurityTesting = (props) => {
  return (
    <div className="llm-security-container1">
      <Helmet>
        <title>Cysigil Network Pvt Ltd</title>
        <meta
          property="og:title"
          content="LLM Security Testing - Cysigil"
        />
        <link
          rel="canonical"
          href="https://cysigil.com/llm-security-testing"
        />
      </Helmet>

      <Navigation />

      {/* HERO */}
      <section className="hero-llm">
        <video
          src="/external/llm-security.mp4"
          loop
          muted
          autoPlay
          playsInline
          className="hero-llm-video"
        />
        <div className="hero-llm-overlay"></div>
        <div className="hero-llm-content">
          <div className="hero-llm-text">
            <h1 className="hero-title">LLM & Generative AI Security Testing</h1>
            <p className="hero-subtitle">
              Comprehensive security, privacy, and abuse testing for Large
              Language Models (LLMs), GenAI applications, AI agents, and
              AI-powered platforms. Identify risks before attackers and
              regulators do.
            </p>
            <div className="hero-llm-actions">
              <a href="#contact">
                <div className="btn-primary btn btn-lg">
                  <span>Request Assessment</span>
                </div>
              </a>
              <a href="#contact">
                <div className="btn btn-lg btn-outline">
                  <span style={{ color: 'black' }}>Talk to Expert</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-llm">
        <div className="features-llm-container">
          <div className="features-llm-header">
            <h2 className="section-title">
              Comprehensive LLM Security Scope
            </h2>
            <p className="section-subtitle">
              End-to-end security testing across AI models, prompts, APIs,
              pipelines, and integrations
            </p>
          </div>

          <div className="features-llm-grid">
            <div className="features-llm-item">
              <h3 className="features-llm-title">Prompt Injection Testing</h3>
              <p className="section-content">
                Identification of direct and indirect prompt injection
                vulnerabilities that allow instruction override, data leakage,
                or unauthorized actions.
              </p>
            </div>

            <div className="features-llm-item">
              <h3 className="features-llm-title">Model Abuse & Jailbreaks</h3>
              <p className="section-content">
                Testing against jailbreak techniques, policy bypass, content
                filtering evasion, and unsafe response generation.
              </p>
            </div>

            <div className="features-llm-item">
              <h3 className="features-llm-title">Data Leakage & Privacy</h3>
              <p className="section-content">
                Detection of PII leakage, training data inference, and sensitive
                context exposure from prompts, memory, and embeddings.
              </p>
            </div>

            <div className="features-llm-item">
              <h3 className="features-llm-title">LLM API Security</h3>
              <p className="section-content">
                Security assessment of OpenAI, Azure OpenAI, Anthropic, and
                custom LLM APIs including auth, rate limits, and abuse controls.
              </p>
            </div>

            <div className="features-llm-item">
              <h3 className="features-llm-title">AI Agent Security</h3>
              <p className="section-content">
                Testing of tool usage, plugin access, agent chaining, and
                privilege escalation via autonomous agents.
              </p>
            </div>

            <div className="features-llm-item">
              <h3 className="features-llm-title">RAG Pipeline Security</h3>
              <p className="section-content">
                Assessment of vector databases, document ingestion, embeddings,
                and retrieval pipelines for poisoning and manipulation risks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="process-llm">
        <div className="process-llm-container">
          <div className="process-llm-header">
            <h2 className="section-title">
              Threat Modeling & Risk Assessment
            </h2>
            <p className="section-subtitle">
              Structured AI threat modeling aligned with OWASP Top 10 for LLMs
              and emerging AI risk frameworks
            </p>
          </div>

          <div className="process-llm-steps">
            <div className="process-llm-cards-row">
              <div className="process-llm-card animate-card">
                <span className="process-llm-card-number">01</span>
                <h3 className="process-llm-card-title">Architecture & Data Flow Review</h3>
                <p className="section-content">
                  Analyze model architecture, prompt flow, data sources, tools, plugins, and external integrations.
                </p>
              </div>
              <div className="process-llm-card animate-card">
                <span className="process-llm-card-number">02</span>
                <h3 className="process-llm-card-title">Threat Modeling</h3>
                <p className="section-content">
                  Identify abuse cases including prompt injection, model extraction, hallucination exploitation, and data poisoning.
                </p>
              </div>
              <div className="process-llm-card animate-card">
                <span className="process-llm-card-number">03</span>
                <h3 className="process-llm-card-title">Security Testing</h3>
                <p className="section-content">
                  Manual and automated testing using red-teaming, adversarial prompts, and LLM-specific attack techniques.
                </p>
              </div>
              <div className="process-llm-card animate-card">
                <span className="process-llm-card-number">04</span>
                <h3 className="process-llm-card-title">Risk Classification</h3>
                <p className="section-content">
                  Severity-based classification aligned to business impact, regulatory exposure, and exploitability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="compliance-llm">
        <div className="compliance-llm-container">
          <h2 className="section-title">Compliance & AI Governance Alignment</h2>

          <table className="compliance-llm-table">
            <thead>
              <tr>
                <th>Framework</th>
                <th>LLM Security Coverage</th>
                <th>Support</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ISO 27001</td>
                <td>AI system security, access control, logging</td>
                <td>Audit Ready</td>
              </tr>
              <tr>
                <td>SOC 2 Type II</td>
                <td>Security, availability, confidentiality</td>
                <td>Evidence Support</td>
              </tr>
              <tr>
                <td>GDPR / DPDP Act</td>
                <td>PII leakage, consent, data minimization</td>
                <td>Regulatory Defense</td>
              </tr>
              <tr>
                <td>OWASP Top 10 for LLMs</td>
                <td>Prompt injection, model abuse, data leakage</td>
                <td>Testing Coverage</td>
              </tr>
              <tr>
                <td>AI GRC</td>
                <td>Model governance, risk & accountability</td>
                <td>Framework Design</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-llm">
        <h2 className="section-title">
          Secure Your AI & LLM Applications
        </h2>
        <p className="section-subtitle">
          Work with Cysigil’s AI security specialists to make your GenAI systems
          secure, compliant, and enterprise-ready.
        </p>
        <a href="#contact">
          <div className="btn-primary btn btn-lg">
            <span>Schedule Consultation</span>
          </div>
        </a>
      </section>

      {/* ANIMATIONS (same logic, different selectors) */}
      <Script
        html={`<script defer>
(function(){
  const items = document.querySelectorAll(
    ".features-llm-item, .process-llm-step"
  )
  const observer = new IntersectionObserver((entries)=>{
    entries.forEach(e=>{
      if(e.isIntersecting){
        e.target.style.opacity="1"
        e.target.style.transform="translateY(0)"
      }
    })
  },{threshold:0.15})

  items.forEach((el,i)=>{
    el.style.opacity="0"
    el.style.transform="translateY(20px)"
    el.style.transition=\0.5s ease \${i*0.1}s\
    observer.observe(el)
  })
})()
</script>`}
      />

      <Footer />
    </div>
  )
}

export default LLMSecurityTesting